import { type ReactNode } from 'react';

import CareSearchStage from '@/cam-admin/care-search/CareSearchStage';
import CareCirclesStage from '@/cam-admin/care-circles/CareCirclesStage';
import DescopingStage from '@/cam-admin/descoping/DescopingStage';
import ShortlistStage from '@/cam-admin/paywall/ShortlistStage';
import ProviderStage from '@/cam-admin/paywall/ProviderStage';
import NorthStarApp from '@/north-star/NorthStarApp';
import KeniBenefitsJourney from '@/benefits-flow/KeniBenefitsJourney';
import Playground from '@/designs-dev/playground/Playground';
import GettingInStage from '@/karehero-app/access/GettingInStage';
import ArchitectureApp from '@/karehero-app/architecture/ArchitectureApp';
import ArchitectureCareApp from '@/karehero-app/architecture/ArchitectureCareApp';
import SiteMockups from '@/karehero-app/site-mockups/SiteMockups';
import SiteHeaders from '@/karehero-app/site-headers/SiteHeaders';
import PetClaude from '@/designs-dev/pet-claude/PetClaude';
import LlamaSandbox from '@/karehero-app/care/LlamaSandbox';
import ProductDesignMeme from '@/designs-dev/meme/ProductDesignMeme';
import IntakeProfileReview from '@/karehero-app/tell-us-once/IntakeProfileReview';
import ComponentsPage from '@/designs-dev/audit/ComponentsPage';
import {
  onboarding20Components,
  onboarding20ThemeChanges,
} from '@/karehero-app/tell-us-once/audit/onboarding20Components';
import OnboardingWalkthrough from '@/karehero-app/tell-us-once/walkthrough/OnboardingWalkthrough';
import CareHubWalkthrough from '@/karehero-app/care/CareHubWalkthrough';

import IfaPortalShell from '@/james-playground/call-to-claim/IfaPortalShell';
import AaUpload from '@/james-playground/call-to-claim/Upload';
import AaProcessing from '@/james-playground/call-to-claim/Processing';
import AaReview from '@/james-playground/call-to-claim/Review';
import MagicCapture from '@/james-playground/magic-capture/MagicCapture';
import BlankDevice from '@/james-playground/blank-device/BlankDevice';
import AaFamilyClaimStage from '@/james-playground/aa-family-claim/AaFamilyClaimStage';
import AdviserStage from '@/james-playground/aa-family-claim/AdviserStage';
import { familyAaClaimSpec } from '@/james-playground/aa-family-claim/AaFamilyClaim.flows';
import { voiceAgentDemoSpec } from '@/karehero-app/voice-agent/VoiceAgentDemo.flows';
import { healthSummarySpec } from '@/karehero-app/voice-agent/HealthSummary.flows';

import PlanPage from '@/designs-dev/plan/PlanPage';
import { tellUsOncePlan } from '@/karehero-app/plan/tell-us-once';
import { tellUsOncePreviews } from '@/karehero-app/plan/previews';

import ExplorationPage from '@/designs-dev/explore/ExplorationPage';
import { tellUsOnceSpec } from '@/karehero-app/explore/tell-us-once/tell-us-once';
import { benefitsCalculatorSpec } from '@/karehero-app/explore/benefits-calculator/benefits-calculator';
import { paywallSpec } from '@/cam-admin/explore/paywall/paywall';

import HandoverPage from '@/designs-dev/handover/HandoverPage';
import VersionedHandoverPage from '@/designs-dev/handover/VersionedHandoverPage';
import { careShortlistsSpec } from '@/karehero-app/handover/care-shortlists';
import { careShortlistsV1Spec } from '@/karehero-app/handover/care-shortlists-v1';
import { careDocumentsSpec } from '@/karehero-app/handover/care-documents';
import { careCallsSpec } from '@/karehero-app/handover/care-calls';
import { camCareSearchSpec } from '@/cam-admin/handover/cam-care-search';
import { documentsSpec } from '@/cam-admin/handover/documents';
import { callsSpec } from '@/cam-admin/handover/calls';
import { notificationsSpec } from '@/cam-admin/handover/notifications';
import { leaveCoverSpec } from '@/cam-admin/handover/leave-cover';
import { onboardingSpec } from '@/karehero-app/handover/onboarding';
import {
  masterActionsSpec,
  masterProfileSpec,
  masterCallsSpec,
  masterDocsSpec,
  masterSettingsSpec,
  masterHelpSpec,
  masterBenefitsCalculatorSpec,
  masterAttendanceAllowanceSpec,
  masterCareCirclesSpec,
  masterOnboardingSpec,
  masterPreRegistrationSpec,
  masterRegistrationSpec,
} from '@/karehero-app/handover/masters';
import { onboardingVariantsSpec } from '@/karehero-app/handover/onboarding-variants';
import { signUpSpec } from '@/karehero-app/handover/sign-up';
import { forgotPasswordSpec } from '@/karehero-app/handover/forgot-password';
import { preSignUpSpec } from '@/karehero-app/handover/pre-sign-up';
import { actionsSpec } from '@/karehero-app/handover/actions';
import { financialAssessmentSpec } from '@/karehero-app/handover/financial-assessment';
import {
  projectMcCamSpec,
  projectMcMemberSpec,
  projectMcProviderSpec,
} from '@/cam-admin/handover/project-mc';
import { v1IntakeSpec } from '@/karehero-app/handover/v1-intake';
import { intakeProfileReviewSpec } from '@/karehero-app/handover/intake-profile-review';
import { onboarding11Spec } from '@/karehero-app/handover/onboarding-1-1';
import { onboarding20Spec } from '@/karehero-app/handover/onboarding-2-0';
import { onboarding21Spec } from '@/karehero-app/handover/onboarding-2-1';
import {
  abTestVersions,
  onboardingVersions,
  patternsVersions,
} from '@/karehero-app/handover/onboarding-versions';
import { dynamicFlowSpec } from '@/karehero-app/handover/dynamic-flow';
import { dynamicFlowSlicesSpec } from '@/karehero-app/handover/dynamic-flow-slices';
import { dynamicFlowAppSpec } from '@/karehero-app/handover/dynamic-flow-app';
import { careHubSpec } from '@/karehero-app/handover/care-hub';
import { dynamicFlowBrainstormSpec } from '@/karehero-app/handover/dynamic-flow-brainstorm';
import { patterns20Spec } from '@/karehero-app/handover/patterns-2-0';
import { interactions20Spec } from '@/karehero-app/handover/interactions-2-0';
import { benefitsCalculatorFlowsSpec } from '@/karehero-app/handover/benefits-calculator';
import { benefitsCalculatorVersions } from '@/karehero-app/handover/benefits-calculator-versions';
import { camOverviewSpec } from '@/karehero-app/handover/cam-overview';
import { attendanceAllowanceFlowsSpec } from '@/karehero-app/handover/attendance-allowance';
import { patternsSpec } from '@/karehero-app/handover/patterns';

// What each screen and each handover renders, keyed by the path the screen used
// to live at and by the handover's id. The manifest holds the structure and this
// holds the components, so adding a project is one edit to projects.ts plus one
// line here, and neither file has to know how routes are assembled.
//
// The member app is absent on purpose: it runs its own router under
// /karehero-app/*, and moving its screens onto the scheme is its own step.
export const SCREEN_ELEMENTS: Record<string, ReactNode> = {
  '/care-search': <CareSearchStage />,
  '/care-search/notifications': <CareSearchStage initialView='notifications' />,
  '/care-circles': <CareCirclesStage />,
  '/descoping-cam': <DescopingStage />,
  '/paywall-shortlist': <ShortlistStage />,
  '/paywall-provider': <ProviderStage />,
  '/north-star': <NorthStarApp />,
  '/benefits-flow': <KeniBenefitsJourney />,
  '/playground': <Playground />,
  // Keyed by where it lives, since it has never lived anywhere else. That is
  // '/onboarding' rather than '/onboarding/prototype': the version holds one
  // screen, so the scheme drops the screen segment and the project page and the
  // screen are the same page.
  '/onboarding': <GettingInStage />,
  '/sandbox/architecture': <ArchitectureApp />,
  '/sandbox/architecture-care': <ArchitectureCareApp />,
  '/sandbox/site-mockups': <SiteMockups />,
  '/sandbox/site-headers': <SiteHeaders />,
  '/sandbox/pet-claude': <PetClaude />,
  '/sandbox/llama': <LlamaSandbox />,
  '/sandbox/product-design-meme': <ProductDesignMeme />,
  '/tell-us-once/plan': (
    <PlanPage spec={tellUsOncePlan} previews={tellUsOncePreviews} />
  ),
  '/tell-us-once/walkthrough': <OnboardingWalkthrough />,
  '/tell-us-once/joining-up-walkthrough': <CareHubWalkthrough />,
  '/tell-us-once/components': (
    <ComponentsPage
      title='Onboarding 2.0 components'
      components={onboarding20Components}
      themeChanges={onboarding20ThemeChanges}
    />
  ),
  '/tell-us-once/intake-profile-review': <IntakeProfileReview />,
  // Tell Us Once's own mount of the CAM control panel, opened where this
  // project's work is: the Overview tab, with an intake behind it.
  '/tell-us-once/cam-prototype': (
    <CareSearchStage defaultTab='overview' sceneSet='intake' inProjectCircles />
  ),
  '/aa-claim': <IfaPortalShell />,
  '/aa-claim/upload': <AaUpload />,
  '/aa-claim/processing': <AaProcessing />,
  '/aa-claim/review': <AaReview />,
  '/magic-capture': <MagicCapture />,
  '/james-playground': <BlankDevice />,
  '/sandbox/family-aa-claim': <AaFamilyClaimStage />,
  '/sandbox/family-aa-claim-adviser': <AdviserStage />,
  '/sandbox/family-aa-claim-flows': <HandoverPage spec={familyAaClaimSpec} />,
  '/sandbox/voice-agent-demo': <HandoverPage spec={voiceAgentDemoSpec} />,
  '/sandbox/voice-agent-health-summary': (
    <HandoverPage spec={healthSummarySpec} />
  ),
  '/explore/tell-us-once': <ExplorationPage spec={tellUsOnceSpec} />,
  '/explore/tell-us-once/benefits-calculator': (
    <ExplorationPage spec={benefitsCalculatorSpec} />
  ),
  '/explore/blurred-profile-paywall': <ExplorationPage spec={paywallSpec} />,
};

export const HANDOVER_ELEMENTS: Record<string, ReactNode> = {
  'master-pre-registration': <HandoverPage spec={masterPreRegistrationSpec} />,
  'master-registration': <HandoverPage spec={masterRegistrationSpec} />,
  'master-onboarding': <HandoverPage spec={masterOnboardingSpec} />,
  'master-actions': <HandoverPage spec={masterActionsSpec} />,
  'master-profile': <HandoverPage spec={masterProfileSpec} />,
  'master-calls': <HandoverPage spec={masterCallsSpec} />,
  'master-docs': <HandoverPage spec={masterDocsSpec} />,
  'master-settings': <HandoverPage spec={masterSettingsSpec} />,
  'master-help': <HandoverPage spec={masterHelpSpec} />,
  'master-benefits-calculator': (
    <HandoverPage spec={masterBenefitsCalculatorSpec} />
  ),
  'master-attendance-allowance': (
    <HandoverPage spec={masterAttendanceAllowanceSpec} />
  ),
  'master-care-circles': <HandoverPage spec={masterCareCirclesSpec} />,
  'onboarding-variants': <HandoverPage spec={onboardingVariantsSpec} />,
  actions: <HandoverPage spec={actionsSpec} />,
  'care-shortlists': <HandoverPage spec={careShortlistsSpec} />,
  'care-shortlists-v1': <HandoverPage spec={careShortlistsV1Spec} />,
  'care-documents': <HandoverPage spec={careDocumentsSpec} />,
  'care-calls': <HandoverPage spec={careCallsSpec} />,
  onboarding: <HandoverPage spec={onboardingSpec} />,
  'pre-sign-up': <HandoverPage spec={preSignUpSpec} />,
  'sign-up': <HandoverPage spec={signUpSpec} />,
  'forgot-password': <HandoverPage spec={forgotPasswordSpec} />,
  'financial-assessment': <HandoverPage spec={financialAssessmentSpec} />,
  'cam-care-search': <HandoverPage spec={camCareSearchSpec} />,
  documents: <HandoverPage spec={documentsSpec} />,
  calls: <HandoverPage spec={callsSpec} />,
  notifications: <HandoverPage spec={notificationsSpec} />,
  caseloads: <HandoverPage spec={leaveCoverSpec} />,
  'project-mc-provider': <HandoverPage spec={projectMcProviderSpec} />,
  'project-mc-cam': <HandoverPage spec={projectMcCamSpec} />,
  'project-mc-member': <HandoverPage spec={projectMcMemberSpec} />,
  'v1-intake': <HandoverPage spec={v1IntakeSpec} />,
  'intake-profile-review': <HandoverPage spec={intakeProfileReviewSpec} />,
  'onboarding-1-1': <HandoverPage spec={onboarding11Spec} />,
  'onboarding-2-0': <HandoverPage spec={onboarding20Spec} />,
  'onboarding-2-1': <HandoverPage spec={onboarding21Spec} />,
  'onboarding-chat': <VersionedHandoverPage spec={onboardingVersions} />,
  'onboarding-ab-test': <VersionedHandoverPage spec={abTestVersions} />,
  'onboarding-patterns': <VersionedHandoverPage spec={patternsVersions} />,
  'dynamic-flow': <HandoverPage spec={dynamicFlowSpec} />,
  'dynamic-flow-slices': <HandoverPage spec={dynamicFlowSlicesSpec} />,
  'dynamic-flow-app': <HandoverPage spec={dynamicFlowAppSpec} />,
  'joining-up': <HandoverPage spec={careHubSpec} />,
  'dynamic-flow-brainstorm': <HandoverPage spec={dynamicFlowBrainstormSpec} />,
  'patterns-2-0': <HandoverPage spec={patterns20Spec} />,
  'interactions-2-0': <HandoverPage spec={interactions20Spec} />,
  'cam-overview': <HandoverPage spec={camOverviewSpec} />,
  'benefits-calculator': (
    <VersionedHandoverPage spec={benefitsCalculatorVersions} />
  ),
  'benefits-calculator-concept': (
    <HandoverPage spec={benefitsCalculatorFlowsSpec} />
  ),
  'attendance-allowance': <HandoverPage spec={attendanceAllowanceFlowsSpec} />,
  patterns: <HandoverPage spec={patternsSpec} />,
};
